import { useState, useEffect } from 'react';
import { InstallSplash } from './components/InstallSplash';
import { InstallBanner } from './components/InstallBanner';
import { DashboardView } from './components/DashboardView';
import { MatrixView } from './components/MatrixView';
import { findBestTeamByEmail } from './lib/teamUtils';
import { TeamConfig } from './components/TeamConfig';
import { PointsConfig } from './components/PointsConfig';
import { TasksConfig } from './components/TasksConfig';
import { MapView } from './components/MapView';
import { MapViewMapbox } from './components/MapViewMapbox';
import { GanttTimeline } from './components/GanttTimeline';
import { SlotPlannerView } from './components/SlotPlannerView';
import { CategoriesConfig } from './components/CategoriesConfig';
import { DataManagement } from './components/DataManagement';
import { AppearanceSettings } from './components/AppearanceSettings';
import { TeamApp } from './components/TeamApp';
import { ArtistApp } from './components/ArtistApp';
import { MessagesView } from './components/MessagesView';
import { Auth } from './components/Auth';
import { EmailVerificationGate } from './components/EmailVerificationGate';
import { TeamJoinPage } from './components/TeamJoinPage';
import { MessagingDrawer } from './components/MessagingDrawer';
import { OrganizationsConfig } from './components/OrganizationsConfig';
import { AuditView } from './components/AuditView';
import { CustomFieldsConfig } from './components/CustomFieldsConfig';
import { ImportTeam } from './components/ImportTeam';
import { ImportArtists } from './components/ImportArtists';
import { ImportPlaces } from './components/ImportPlaces';
import { SetupGuide, SetupGuideFloatButton, SetupGuideBanner, getGuideTabForStep } from './components/SetupGuide';
import { useSetupGuide } from './hooks/useSetupGuide';
import { Calendar, Users, MapPin, Map, Clock, Shield, Settings, LogOut, Database, LayoutDashboard, Menu, X, Palette, Bell, Building2, ExternalLink, UserCircle, MessageSquare, Gift, Music, ChevronDown, FolderKanban, Eye, FileText, Upload, ClipboardList } from 'lucide-react';
import { ProjectManagementModal } from './components/ProjectManagementModal';
import { TeamOffersView } from './components/TeamOffersView';
import { ArtistManagementView } from './components/ArtistManagementView';
import { EmbedMapView } from './components/EmbedMapView';
import { EmbedMapConfig } from './components/EmbedMapConfig';

import { useStore } from './store/useStore';
import { useSupabaseSync } from './hooks/useSupabaseSync';
import { useDynamicFavicon } from './hooks/useDynamicFavicon';
import { usePushNotifications } from './hooks/usePushNotifications';
import { supabase } from './lib/supabase';
import { Session } from '@supabase/supabase-js';

const getSectionForTab = (tab: string): string => {
  if (['dashboard', 'matrix', 'team_timeline', 'slot_planner', 'map'].includes(tab)) return 'operations';
  if (['messages', 'team_offers'].includes(tab)) return 'communication';
  if (['artist_management'].includes(tab)) return 'artists';
  if (['team_conf', 'points_conf', 'tasks_conf', 'categories_conf', 'appearance', 'custom_fields', 'import_team', 'import_artists', 'import_places'].includes(tab)) return 'config';
  if (['data_management', 'organizations', 'audit', 'embed_map'].includes(tab)) return 'superadmin';
  return 'operations';
};

function App() {
  // ── Early check: public invitation page (bypasses auth) ──────────
  const _inviteParam = new URLSearchParams(window.location.search).get('invite');
  if (_inviteParam) {
    return <TeamJoinPage categoryId={_inviteParam} />;
  }

  // ── Early check: public embed map page (bypasses auth) ───────────
  const _embedParam = new URLSearchParams(window.location.search).get('embed');
  if (_embedParam) {
    return <EmbedMapView orgId={_embedParam} />;
  }

  const [activeTab, setActiveTab] = useState<'dashboard' | 'matrix' | 'team_timeline' | 'slot_planner' | 'map' | 'team_conf' | 'points_conf' | 'tasks_conf' | 'categories_conf' | 'data_management' | 'appearance' | 'messages' | 'organizations' | 'team_offers' | 'artist_management' | 'audit' | 'custom_fields' | 'import_team' | 'import_artists' | 'import_places' | 'embed_map'>(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const allowedTabs = [
      'dashboard', 'matrix', 'team_timeline', 'map', 'team_conf', 
      'points_conf', 'tasks_conf', 'categories_conf', 'data_management', 'appearance',
      'messages', 'organizations', 'team_offers', 'artist_management', 'audit',
      'slot_planner', 'custom_fields', 'import_team', 'import_artists', 'import_places', 'embed_map'
    ];
    if (tabParam && allowedTabs.includes(tabParam)) return tabParam as any;
    return 'dashboard';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isAccountSwitcherOpen, setIsAccountSwitcherOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initialSection = getSectionForTab(activeTab);
    return {
      operations: initialSection === 'operations',
      config: initialSection === 'config',
      superadmin: initialSection === 'superadmin',
      artists: initialSection === 'artists',
      communication: initialSection === 'communication'
    };
  });
  const toggleSection = (key: string) => setOpenSections(prev => {
    const isOpen = prev[key];
    const next: Record<string, boolean> = {};
    Object.keys(prev).forEach(k => {
      next[k] = false;
    });
    next[key] = !isOpen;
    return next;
  });
  const { categories, team, points, shifts, currentUser, setCurrentUser, globalSettings, currentOrgId, setCurrentOrgId, currentProjectId, setCurrentProjectId, projects, impersonatedTeamId, setImpersonatedTeamId, viewAsManagerCategoryId, setViewAsManagerCategoryId, organizations, updateGlobalSettings, messages, messagingDrawer, setMessagingDrawerOpen } = useStore();
  const appLogo = globalSettings?.appLogoUrl;

  // Unify: ALL team records sharing my email → match against all of them
  const myEmail = currentUser.email || localStorage.getItem('wfm_user_email');
  const myTeamIds = new Set(
    myEmail
      ? team.filter(s => s.email?.toLowerCase().trim() === myEmail.toLowerCase().trim()).map(s => s.id)
      : []
  );
  // Unread count: exclude my own outgoing (team + admin) and broadcasts
  const myNames = new Set(team.filter(s => myTeamIds.has(s.id)).map(s => s.name.toLowerCase().trim()));
  const isSentByMeApp = (m: any) => !m.fromTeam && [...myNames].some(n => n && (m.senderName || '').toLowerCase().includes(n));
  const myUnreadCount = (messages || []).filter(m =>
    !m.read && !isSentByMeApp(m) && (m.fromTeam || myTeamIds.has(m.teamId) || (!!m.recipientTeamId && myTeamIds.has(m.recipientTeamId)))
  ).length;

  // ── Global message Realtime + Polling (sidebar dot, notifications) ──
  const isAdminOrManager = currentUser.role === 'admin' || currentUser.role === 'manager';
  useEffect(() => {
    if (!isAdminOrManager) return;
    const visibleIds = new Set(team.map(s => s.id));
    if (visibleIds.size === 0) return;
    let mounted = true;

    const mapMsg = (row: any) => ({
      id: row.id, teamId: row.staff_id, orgId: row.org_id, projectId: row.project_id,
      content: row.content, timestamp: row.created_at, read: row.read ?? false,
      fromTeam: row.from_staff ?? false, senderName: row.sender_name ?? undefined,
      recipientTeamId: row.recipient_staff_id ?? undefined,
      imageUrl: row.image_url ?? null, voiceUrl: row.voice_url ?? null,
    });

    // Realtime: instant message delivery
    const channel = supabase
      .channel('app-messages-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload: any) => {
        if (!mounted) return;
        const row = payload.new;
        if (visibleIds.has(row.staff_id) || myTeamIds.has(row.recipient_staff_id || '')) {
          useStore.setState((s) => {
            if ((s.messages || []).some(m => m.id === row.id)) return s;
            return { messages: [...(s.messages || []), mapMsg(row)] };
          });
        }
      })
      // Realtime: instant issue/alert delivery
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'issues' }, (payload: any) => {
        if (!mounted) return;
        const row = payload.new;
        const newIssue = {
          id: row.id, pointId: row.point_id, teamId: row.staff_id,
          orgId: row.org_id, projectId: row.project_id,
          teamName: team.find(s => s.id === row.staff_id)?.name || 'Inconnu',
          message: row.description, timestamp: row.created_at,
          resolved: row.resolved ?? false, type: row.type || 'alert'
        };
        useStore.setState((s) => {
          if ((s.issues || []).some(i => i.id === row.id)) return s;
          return { issues: [...(s.issues || []), newIssue] };
        });
      })
      .subscribe();

    // Polling backup (30s — réduit de 5s pour économiser l'egress)
    let lastCheck = new Date().toISOString();
    const MSG_COLUMNS = 'id, staff_id, org_id, project_id, content, created_at, read, from_staff, sender_name, recipient_staff_id';
    const poll = async () => {
      if (!mounted || document.hidden) return;
      try {
        const ids = Array.from(visibleIds);
        for (let i = 0; i < ids.length; i += 50) {
          const batch = ids.slice(i, i + 50);
          const { data: newMsgs } = await supabase.from('messages').select(MSG_COLUMNS)
            .in('staff_id', batch).gt('created_at', lastCheck).is('deleted_at', null)
            .order('created_at', { ascending: true });
          if (newMsgs && newMsgs.length > 0 && mounted) {
            const maxCreatedAt = newMsgs.reduce((max: string, row: any) => row.created_at > max ? row.created_at : max, lastCheck);
            if (maxCreatedAt > lastCheck) lastCheck = maxCreatedAt;
            useStore.setState((s) => {
              const existingIds = new Set((s.messages || []).map(m => m.id));
              const fresh = newMsgs.map(mapMsg).filter((m: any) => !existingIds.has(m.id));
              return fresh.length > 0 ? { messages: [...(s.messages || []), ...fresh] } : s;
            });
          }
        }
      } catch (_) { /* ignore */ }
    };
    const interval = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(interval); supabase.removeChannel(channel); };
  }, [isAdminOrManager, team.length]);

  // Sync favicon & apple-touch-icon with the uploaded logo
  useDynamicFavicon(appLogo);
  
  const [session, setSession] = useState<Session | null>(null);

  const INSTALL_SPLASH_KEY = 'wfm_install_splash_shown';
  const [showInstallSplash, setShowInstallSplash] = useState(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    return !isStandalone && !localStorage.getItem(INSTALL_SPLASH_KEY);
  });
  const handleInstallDone = () => {
    localStorage.setItem(INSTALL_SPLASH_KEY, '1');
    setShowInstallSplash(false);
  };

  // Notifications logic (must be before any early returns)
  const normalizedUserEmail = (session?.user?.is_anonymous 
    ? localStorage.getItem('wfm_user_email') 
    : session?.user?.email)?.toLowerCase().trim();

  // Auto-sync superadmin email to localStorage if verified via Supabase session
  useEffect(() => {
    if (normalizedUserEmail === 'benmloukaamen@gmail.com') {
      if (typeof window !== 'undefined' && localStorage.getItem('wfm_user_email') !== 'benmloukaamen@gmail.com') {
        localStorage.setItem('wfm_user_email', 'benmloukaamen@gmail.com');
      }
    }
  }, [normalizedUserEmail]);

  const userTeamMember = findBestTeamByEmail(team, normalizedUserEmail, currentOrgId);
  const { permissionState, fcmReady, fcmError, fcmStep, requestPermission } = usePushNotifications(userTeamMember?.id);

  const [accessDenied, setAccessDenied] = useState(false);
  const [managerToken, setManagerToken] = useState<string | null>(
    () => localStorage.getItem('wfm_manager_token')
  );
  const [teamToken, setTeamToken] = useState<string | null>(
    () => localStorage.getItem('wfm_staff_token')
  );
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const [needEmailVerification, setNeedEmailVerification] = useState<{ type: 'admin' | 'manager', token: string } | null>(null);
  const [resolvedManagerInfo, setResolvedManagerInfo] = useState<{ categoryId: string, orgId: string, projectId?: string } | null>(null);

  const [isInitializing, setIsInitializing] = useState(true);
  const { isSynced, refresh } = useSupabaseSync(session, isInitializing);

  // Refresh on tab change
  useEffect(() => {
    if (isSynced) refresh();
  }, [activeTab]);

  // Auto-refresh after 2min of inactivity
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { if (isSynced) refresh(); }, 120000);
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isSynced, refresh]);

  // Setup guide — must be called unconditionally (Rules of Hooks)
  const guide = useSetupGuide(currentUser.categoryId);

  // Unified Auth & Token logic
  useEffect(() => {
    // 1. Setup Global Listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setSession(newSession);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
      }
    });

    async function init() {
      const params = new URLSearchParams(window.location.search);
      const newUrl = new URL(window.location.href);
      let needsRefresh = false;

      const { data: { session: currentSession } } = await supabase.auth.getSession();
      let activeSession = currentSession;

      // 1. Extract tokens from URL
      const oToken = params.get('org_token');
      if (oToken) {
        // If we have an active session that is NOT anonymous, we MUST sign out to switch to the org context
        if (activeSession && !activeSession.user.is_anonymous && activeSession.user.email?.toLowerCase().trim() !== 'benmloukaamen@gmail.com') {
           await supabase.auth.signOut();
           window.location.reload();
           return;
        }

        localStorage.setItem('wfm_org_token', oToken);
        localStorage.removeItem('wfm_manager_token');
        localStorage.removeItem('wfm_staff_token');
        localStorage.removeItem('wfm_user_email');
        localStorage.removeItem('wfm_device_id');
        localStorage.removeItem('wfm_current_org_id');
        localStorage.removeItem('wfm_project_id');
        setCurrentOrgId(null);
        newUrl.searchParams.delete('org_token');
        needsRefresh = true;
      }
      const mToken = params.get('manager_token');
      if (mToken) {
        localStorage.setItem('wfm_manager_token', mToken);
        localStorage.removeItem('wfm_org_token');
        localStorage.removeItem('wfm_staff_token');
        localStorage.removeItem('wfm_user_email');
        localStorage.removeItem('wfm_device_id');
        localStorage.removeItem('wfm_current_org_id');
        localStorage.removeItem('wfm_project_id');
        setCurrentOrgId(null);
        newUrl.searchParams.delete('manager_token');
        needsRefresh = true;
      }
      // Support staff_token (canonical), team_token, and art_token (alias) query params
      const sToken = params.get('staff_token') || params.get('team_token') || params.get('art_token');
      if (sToken) {
        localStorage.setItem('wfm_staff_token', sToken);
        localStorage.removeItem('wfm_org_token');
        localStorage.removeItem('wfm_manager_token');
        localStorage.removeItem('wfm_user_email');
        localStorage.removeItem('wfm_device_id');
        localStorage.removeItem('wfm_current_org_id');
        localStorage.removeItem('wfm_project_id');
        setCurrentOrgId(null);
        newUrl.searchParams.delete('staff_token');
        newUrl.searchParams.delete('team_token');
        newUrl.searchParams.delete('art_token');
        needsRefresh = true;
      }

      if (needsRefresh) {
        window.history.replaceState({}, document.title, newUrl.toString());
      }

      // 3. If no session but tokens exist, sign in anonymously
      const hasAnyToken = !!(localStorage.getItem('wfm_org_token') || localStorage.getItem('wfm_manager_token') || localStorage.getItem('wfm_staff_token'));
      
      if (!activeSession && hasAnyToken) {
        const { data: { session: anonymousSession } } = await supabase.auth.signInAnonymously();
        activeSession = anonymousSession;
      }

      // Resolve org from token and update JWT claims for RLS
      // IMPORTANT: always resolve even if currentOrgId is already set, because
      // the anonymous session needs org_id in user_metadata for RLS policies.
      const activeOrgToken = localStorage.getItem('wfm_org_token');
      const storeOrgId = useStore.getState().currentOrgId;
      console.log('[App:init] activeOrgToken:', activeOrgToken?.substring(0, 8) + '...', 'storeOrgId:', storeOrgId);
      if (activeOrgToken) {
        // First try organizations.admin_token
        const { data: orgData } = await supabase.from('organizations')
          .select('id')
          .eq('admin_token', activeOrgToken)
          .maybeSingle();

        console.log('[App:init] org resolution (admin_token):', orgData);

        // If not found, try organization_admins.token (email-verified admins)
        let resolvedOrgId = orgData?.id || null;
        if (!resolvedOrgId) {
          const { data: adminData } = await supabase.from('organization_admins')
            .select('org_id')
            .eq('token', activeOrgToken)
            .is('deleted_at', null)
            .maybeSingle();
          console.log('[App:init] org resolution (org_admins token):', adminData);
          resolvedOrgId = adminData?.org_id || null;
        }

        if (resolvedOrgId) {
          if (!storeOrgId) {
            setCurrentOrgId(resolvedOrgId);
            console.log('[App:init] setCurrentOrgId:', resolvedOrgId);
          }
          // TAG the session with the orgId in user_metadata (critical for RLS)
          await supabase.auth.updateUser({
            data: { org_id: resolvedOrgId }
          });
          // Refresh session to apply the org_id claim to the JWT
          await supabase.auth.refreshSession();
        }
      }

      // 4. Final session sync & claim
      if (activeSession) {
        setSession(activeSession);
        
        const o = localStorage.getItem('wfm_org_token');
        const m = localStorage.getItem('wfm_manager_token');
        const s = localStorage.getItem('wfm_staff_token');
        const email = localStorage.getItem('wfm_user_email');
        
        try {
          if (o) await supabase.rpc('claim_token', { p_token: o, p_type: 'manager' });
          if (m) await supabase.rpc('claim_token', { p_token: m, p_type: 'manager' });
          if (s) await supabase.rpc('claim_token', { p_token: s, p_type: 'staff' });
        } catch (e: any) {
          // Silent error
        }

        // Resolve manager token details if active
        let managerInfo = null;
        if (m) {
          try {
            const { data: managerRecs } = await supabase
              .rpc('resolve_manager_token', { p_token: m });
            
            if (managerRecs && managerRecs.length > 0) {
              managerInfo = {
                categoryId: managerRecs[0].category_id,
                orgId: managerRecs[0].org_id,
                projectId: managerRecs[0].project_id
              };
              setResolvedManagerInfo(managerInfo);
            }
          } catch (err) {
            console.error("Error resolving manager token info:", err);
          }
        }

        // Auto-synchronisation des claims de sécurité
        const meta = activeSession.user.user_metadata || {};
        const isSuperAdmin = email?.toLowerCase().trim() === 'benmloukaamen@gmail.com';
        
        let expectedRole = 'staff';
        let expectedTeamId = null;
        let expectedOrgId = null;
        
        const { organizations, team } = useStore.getState();
        
        if (o) {
          expectedRole = 'admin';
          const matchingOrg = organizations.find(org => org.adminToken === o);
          if (matchingOrg) expectedOrgId = matchingOrg.id;
        } else if (m) {
          expectedRole = 'manager';
          if (managerInfo) expectedOrgId = managerInfo.orgId;
        } else if (s) {
          expectedRole = 'staff';
          const matchingTeam = team.find(st => st.teamToken === s);
          if (matchingTeam) {
            expectedTeamId = matchingTeam.id;
            expectedOrgId = matchingTeam.orgId;
          } else {
            // Bypass bootstrap race condition/deadlock by calling RPC resolve_staff_token
            try {
              const { data: teamRecs } = await supabase.rpc('resolve_staff_token', { p_token: s });
              if (teamRecs && teamRecs.length > 0) {
                expectedTeamId = teamRecs[0].id;
                expectedOrgId = teamRecs[0].org_id;
              }
            } catch (err) {
              console.error("Error resolving team token via RPC:", err);
            }
          }
        }
        
        if (
          meta.role !== (isSuperAdmin ? 'superadmin' : expectedRole) ||
          meta.org_id !== expectedOrgId ||
          meta.staff_id !== expectedTeamId ||
          meta.email !== email
        ) {
          await supabase.auth.updateUser({
            data: {
              email: email,
              role: isSuperAdmin ? 'superadmin' : expectedRole,
              staff_id: expectedTeamId,
              org_id: expectedOrgId
            }
          });
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          if (refreshed) {
            setSession(refreshed);
          }
        }
      }

      // 5. Verification check for Admins and Managers
      const activeO = localStorage.getItem('wfm_org_token');
      const activeM = localStorage.getItem('wfm_manager_token');
      const email = localStorage.getItem('wfm_user_email');
      const isSuperAdmin = email?.toLowerCase().trim() === 'benmloukaamen@gmail.com';

      if (!isSuperAdmin) {
        if (activeO) {
          const { data: adminRecord } = await supabase
            .from('organization_admins')
            .select('email_confirmed, email')
            .eq('token', activeO)
            .is('deleted_at', null)
            .maybeSingle();
          if (adminRecord && !adminRecord.email_confirmed) {
            setNeedEmailVerification({ type: 'admin', token: activeO });
          }
          // Restore wfm_user_email for account switcher (may have been cleared by old code)
          if (adminRecord?.email && !localStorage.getItem('wfm_user_email')) {
            localStorage.setItem('wfm_user_email', adminRecord.email.toLowerCase().trim());
          }
        } else if (activeM) {
          const { data: managerRecord } = await supabase
            .from('category_managers')
            .select('email_confirmed, email')
            .eq('token', activeM)
            .is('deleted_at', null)
            .maybeSingle();
          if (managerRecord && !managerRecord.email_confirmed) {
            setNeedEmailVerification({ type: 'manager', token: activeM });
          }
          if (managerRecord?.email && !localStorage.getItem('wfm_user_email')) {
            localStorage.setItem('wfm_user_email', managerRecord.email.toLowerCase().trim());
          }
        }
      }
      
      setIsInitializing(false);
    }

    init();
    return () => subscription.unsubscribe();
  }, []);

  // Removed old auth listener

  // Détection session invalidée — si un autre appareil prend la session, on déconnecte celui-ci
  useEffect(() => {
    if (!currentUser.teamId) return;

    const myDeviceId = localStorage.getItem('wfm_device_id');
    if (!myDeviceId) return;

    const channel = supabase
      .channel('device-session-watch')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'staff',
        filter: `id=eq.${currentUser.teamId}`,
      }, (payload: any) => {
        const newDeviceId = payload.new?.current_device_id;
        if (newDeviceId && newDeviceId !== myDeviceId) {
          setSessionInvalidated(true);
          localStorage.removeItem('wfm_staff_token');
          localStorage.removeItem('wfm_device_id');
          supabase.auth.signOut();
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUser.teamId]);

  const getNormalizedEmail = (e?: string) => {
    if (!e) return '';
    const lower = e.toLowerCase().trim();
    const parts = lower.split('@');
    if (parts.length !== 2) return lower;
    return `${parts[0].split('+')[0]}@${parts[1]}`;
  };

  // Role detection — runs when session, categories, or team list changes
  useEffect(() => {
    if (isInitializing || !isSynced || !session) return;
    
    // ── Anonymous session path ──────────────────────────────────────────
    if (session.user.is_anonymous) {
      const sToken = localStorage.getItem('wfm_staff_token');
      const mToken = localStorage.getItem('wfm_manager_token');
      const oToken = localStorage.getItem('wfm_org_token');
      const savedEmail = localStorage.getItem('wfm_user_email');

      // ── Superadmin check via anonymous OTP email ────────────────
      if (savedEmail === 'benmloukaamen@gmail.com') {
        setCurrentUser({ role: 'admin', email: 'benmloukaamen@gmail.com' });
        if (!currentOrgId && organizations.length > 0) {
          const woodstock = organizations.find(o => o.name === 'WoOdsTock New Age');
          setCurrentOrgId(woodstock ? woodstock.id : organizations[0].id);
        }
        setAccessDenied(false);
        return;
      }

      // ── Org Admin check via anonymous OTP email (only if no specific token) ─────
      if (savedEmail && !sToken && !mToken) {
        const managedOrg = organizations.find(o => o.adminEmail?.toLowerCase().trim() === savedEmail.toLowerCase().trim());
        if (managedOrg) {
          setCurrentUser({ role: 'admin', email: savedEmail });
          if (currentOrgId !== managedOrg.id) {
            setCurrentOrgId(managedOrg.id);
          }
          setAccessDenied(false);
          return;
        }
      }

      // ── Manager token ─────────────────────────────────────────
      if (mToken) {
        if (resolvedManagerInfo) {
          setCurrentUser({ role: 'manager', categoryId: resolvedManagerInfo.categoryId, email: savedEmail || undefined });
          if (resolvedManagerInfo.orgId && currentOrgId !== resolvedManagerInfo.orgId) {
            setCurrentOrgId(resolvedManagerInfo.orgId);
          }
          if (resolvedManagerInfo.projectId && currentProjectId !== resolvedManagerInfo.projectId) {
            setCurrentProjectId(resolvedManagerInfo.projectId);
          }
          setAccessDenied(false);
        } else {
          setAccessDenied(true);
        }
        return;
      }

      // ── Team credentials ─────────────────────────────────────
      if (sToken) {
        const teamMember = team.find(s => s.teamToken === sToken);
        if (teamMember) {
          // Batch role+org update to prevent double render → double sync → React #300
          useStore.setState(s => ({
            currentUser: { role: 'staff' as const, teamId: teamMember.id, email: teamMember.email },
            currentOrgId: teamMember.orgId && s.currentOrgId !== teamMember.orgId ? teamMember.orgId : s.currentOrgId,
          }));
          setAccessDenied(false);
          return;
        } else if (team.length > 0) {
           setAccessDenied(true);
           return;
        }
        return;
      }

      // ── Org token ─────────────────────────────────────────────
      if (oToken) {
        setCurrentUser({ role: 'admin' });
        setAccessDenied(false);
        return;
      }

      // Stale anonymous session with no credentials → sign out
      localStorage.removeItem('wfm_manager_token');
      localStorage.removeItem('wfm_staff_token');
      localStorage.removeItem('wfm_org_token');
      localStorage.removeItem('wfm_current_org_id');
      localStorage.removeItem('wfm_device_id');
      supabase.auth.signOut();
      return;
    }

    // ── Email / password path (admin + team) ──────────────────────────
    if (!session?.user?.email) return;

    const email = session.user.email.toLowerCase().trim();
    const cleanEmail = getNormalizedEmail(email);

    // 1. Superadmin check
    if (cleanEmail === 'benmloukaamen@gmail.com') {
      setCurrentUser({ role: 'admin', email: cleanEmail });
      // If superadmin has no currentOrgId set, or it's not a valid org, default to Woodstock if it exists
      const isValidOrg = organizations.some(o => o.id === currentOrgId);
      if (!isValidOrg && organizations.length > 0) {
        const woodstock = organizations.find(o => o.name === 'WoOdsTock New Age');
        setCurrentOrgId(woodstock ? woodstock.id : organizations[0].id);
      }
      setAccessDenied(false);
      return;
    }

    // 2. Organizational Admin check (by Email)
    const managedOrg = organizations.find(o => o.adminEmail?.toLowerCase().trim() === cleanEmail);
    if (managedOrg) {
      setCurrentUser({ role: 'admin', email: cleanEmail });
      if (currentOrgId !== managedOrg.id) {
        setCurrentOrgId(managedOrg.id);
      }
      setAccessDenied(false);
      return;
    }
    
    // Check if org token is set
    const oToken = localStorage.getItem('wfm_org_token');
    if (oToken) {
      setCurrentUser({ role: 'admin', email: cleanEmail }); // Org admin behaves as admin for their org
      setAccessDenied(false);
      return;
    }

    // Check team list — pick the most complete account if duplicates exist
    const matchingTeam = findBestTeamByEmail(team, cleanEmail, currentOrgId);
    if (matchingTeam) {
      setCurrentUser({ role: 'staff', teamId: matchingTeam.id, email: cleanEmail });
      setAccessDenied(false);
      return;
    }

    // Legacy: check managerEmails (backward compat)
    const legacyCategory = categories.find(c =>
      c.managerEmails?.map(getNormalizedEmail).includes(cleanEmail)
    );
    if (legacyCategory) {
      setCurrentUser({ role: 'manager', categoryId: legacyCategory.id, email: cleanEmail });
      setAccessDenied(false);
      return;
    }

    setAccessDenied(true);
  }, [session, categories, team, setCurrentUser, managerToken, teamToken, isSynced, isInitializing, resolvedManagerInfo]);

  // Synchronize active tab from URL parameters when user role/session is resolved
  useEffect(() => {
    if (isInitializing || !isSynced) return;
    
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    
    if (tabParam) {
      // 1. If it's a manager/admin and they need to see manager/admin tabs
      if (currentUser.role === 'admin' || currentUser.role === 'manager') {
        const allowedTabs = [
          'dashboard', 'matrix', 'team_timeline', 'map', 'team_conf', 
          'points_conf', 'tasks_conf', 'categories_conf', 'data_management', 'appearance',
          'messages', 'organizations', 'team_offers', 'artist_management',
          'import_team', 'import_artists', 'import_places'
        ];
        if (allowedTabs.includes(tabParam)) {
          setActiveTab(tabParam as any);
        }
      }
      
      // 2. If it's the Map tab and we have a point_id to focus
      if (tabParam === 'map') {
        const pointId = params.get('point_id') || params.get('pointId');
        if (pointId) {
          const point = points.find(p => p.id === pointId);
          if (point?.location) {
            useStore.getState().setMapFocus({ lat: point.location.lat, lng: point.location.lng, zoomLevel: 18 });
          }
        }
      }
      
      // 3. Clean up the URL so it remains clean
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('tab');
      newUrl.searchParams.delete('point_id');
      newUrl.searchParams.delete('pointId');
      window.history.replaceState({}, document.title, newUrl.toString());
    }
  }, [session, currentUser.role, isInitializing, isSynced, points]);

  const handleLogout = async () => {
    localStorage.removeItem('wfm_manager_token');
    localStorage.removeItem('wfm_staff_token');
    localStorage.removeItem('wfm_org_token');
    localStorage.removeItem('wfm_current_org_id');
    localStorage.removeItem('wfm_device_id');
    setManagerToken(null);
    setTeamToken(null);
    setCurrentOrgId(null);
    setImpersonatedTeamId(null);
    await supabase.auth.signOut();
  };

  const effectiveCategoryId = (currentUser.role === 'manager' && currentUser.categoryId) || viewAsManagerCategoryId;
  const isEffectiveManager = currentUser.role === 'manager' || !!viewAsManagerCategoryId;

  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'manager' || !!viewAsManagerCategoryId;
  const isArtistManager = isEffectiveManager && categories.find(c => c.id === effectiveCategoryId)?.isArtistCategory;
  const canManageArtists = (currentUser.role === 'admin' && !viewAsManagerCategoryId) || isArtistManager;
  const isSuperAdmin = normalizedUserEmail === 'benmloukaamen@gmail.com';
  const currentOrg = organizations.find(o => o.id === currentOrgId);
  const orgLogo = currentOrg?.logoUrl || '/biplogo.svg';
  const showAppearance = (currentUser.role === 'admin' && !viewAsManagerCategoryId) && (isSuperAdmin || currentOrg?.allowAdminSettings);

  if (isInitializing || !isSynced) {
    return (
      <>
        {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
        <InstallBanner />
        <div className="min-h-screen bg-[#0A0B0E] flex flex-col items-center justify-center p-4">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Database className="w-8 h-8 text-indigo-500 animate-pulse" />
            </div>
          </div>
          <h2 className="mt-8 text-xl font-bold text-white tracking-tight">Synchronisation...</h2>
        </div>
      </>
    );
  }

  if (!session) {
    const isLoggingInWithToken = !!(localStorage.getItem('wfm_manager_token') || localStorage.getItem('wfm_staff_token') || localStorage.getItem('wfm_org_token'));
    
    if (isLoggingInWithToken) {
      return (
        <>
          {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
          <InstallBanner />
          <div className="flex h-[100dvh] w-full items-center justify-center bg-[#0A0B0E]">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-400 text-sm">Authentification par lien...</p>
            </div>
          </div>
        </>
      );
    }

    if (sessionInvalidated) {
      return (
        <>
          {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
          <InstallBanner />
          <div className="flex h-[100dvh] w-full items-center justify-center bg-[#0A0B0E] p-6">
            <div className="text-center max-w-xs">
              <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📱</span>
              </div>
              <h2 className="text-white font-bold text-lg mb-2">Session reprise</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Votre compte a été ouvert sur un autre appareil. Ouvrez à nouveau votre lien d'accès pour vous reconnecter.
              </p>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
        <InstallBanner />
        <Auth />
      </>
    );
  }

  // ── Render: access denied ─────────────────────────────────────────────
  if (accessDenied) {
    return (
      <>
        {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
        <InstallBanner />
        <div className="flex h-[100dvh] w-full items-center justify-center bg-[#0A0B0E] text-white">
          <div className="text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Accès Refusé</h1>
            <p className="text-slate-400 mb-6">
              {session?.user?.is_anonymous
              ? 'Ce lien manager n\'est plus valide ou n\'existe pas.'
              : `Votre email (${session?.user?.email}) n'est assigné à aucun rôle.`}
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded transition-colors"
              >
                Réessayer
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-[#1A1D23] hover:bg-[#2D3139] rounded transition-colors"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: team view ────────────────────────────────────────────────
  if (currentUser.role === 'staff' || impersonatedTeamId) {
    return (
      <>
        {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
        <InstallBanner />
        {impersonatedTeamId && (
          <div className="fixed top-0 inset-x-0 h-10 bg-indigo-600 z-[100] flex items-center justify-between px-4 text-[10px] font-bold uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <UserCircle className="w-4 h-4" />
              Mode Miroir : Team ({team.find(s => s.id === impersonatedTeamId)?.name})
            </div>
            <button 
              onClick={() => setImpersonatedTeamId(null)}
              className="bg-white/10 hover:bg-white/20 px-3 py-1 rounded transition-colors"
            >
              Quitter le mode miroir
            </button>
          </div>
        )}
        <div className={`flex flex-col h-full ${impersonatedTeamId ? 'pt-10' : ''}`}>
          <div className="flex-1 overflow-hidden">
            {/* No loading guard here: the `isSynced` gate above already ensures the sync
                finished. Emptiness is not a loading signal — an organisation with no points
                and no shifts (a brand new one) used to spin here forever. */}
            {userTeamMember?.isArtist ? <ArtistApp key="artist" /> : <TeamApp key="team" />}
          </div>
        </div>
      </>
    );
  }

  const myCategory = categories.find(c => c.id === currentUser.categoryId);

  const managerPoints = points.filter(p => p.categoryId === currentUser.categoryId);
  const showGuideBanner =
    currentUser.role === 'manager' &&
    managerPoints.length === 0 &&
    !guide.state.completed &&
    !guide.state.open;

  // Sidebar highlight: which tab to highlight based on current guide step
  const guideHighlightTab =
    currentUser.role === 'manager' && guide.state.open && !guide.state.completed
      ? getGuideTabForStep(guide.state.step)
      : null;

  // ── Render: email verification gate (admin / manager token validation) ──
  if (needEmailVerification) {
    return (
      <>
        {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
        <InstallBanner />
        <EmailVerificationGate
          type={needEmailVerification.type}
          token={needEmailVerification.token}
          onVerified={() => {
            setNeedEmailVerification(null);
            window.location.reload();
          }}
        />
      </>
    );
  }

  // ── Render: main app (admin + manager) ───────────────────────────────
  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);

    // Automatically open the section for the selected tab and close others
    const section = getSectionForTab(tab);
    setOpenSections(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(k => {
        next[k] = k === section;
      });
      return next;
    });
  };

  const handleGuideNavigate = (tab: typeof activeTab) => {
    handleTabChange(tab);
  };

  return (
    <div className="flex h-[100dvh] w-full bg-[#0A0B0E] text-white overflow-hidden relative">
      {/* PWA Install — visible sur toutes les pages (login, admin, manager, team) */}
      {showInstallSplash && <InstallSplash onContinue={handleInstallDone} />}
      <InstallBanner />

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#14161B] border-r border-[#2D3139] flex flex-col transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;700&display=swap');
            </style>
            {appLogo ? (
              <div className="flex items-center gap-2">
                <img src={appLogo} alt="Logo" className="h-10 w-auto max-w-[160px] object-contain" />
                <span className="text-[22px] font-bold tracking-tight text-white ml-1.5" style={{ fontFamily: '"Fredoka", "Comic Sans MS", cursive, sans-serif' }}>
                  Teams
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <img src={orgLogo} alt="Logo" className="h-10 w-auto" />
                <span className="text-[22px] font-bold tracking-tight text-white ml-1.5" style={{ fontFamily: '"Fredoka", "Comic Sans MS", cursive, sans-serif' }}>
                  Teams
                </span>
              </div>
            )}
          </div>
          <button 
            className="md:hidden p-2 -mr-2 text-slate-400 hover:text-white rounded-lg hover:bg-[#1A1D23]"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Project Selector */}
        <div className="px-3 py-2 border-b border-[#2D3139] bg-[#0A0B0E]/20 flex flex-col gap-1.5 flex-none">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
              <FolderKanban className="w-3.5 h-3.5 text-indigo-400" />
              Projet Actif
            </span>
            {currentUser.role === 'admin' && (
              <button
                onClick={() => setIsProjectModalOpen(true)}
                className="text-[10px] font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
                title="Gérer les projets"
              >
                Gérer
              </button>
            )}
          </div>
          {currentUser.role === 'admin' ? (
            <select
              value={currentProjectId || ''}
              onChange={(e) => {
                setCurrentProjectId(e.target.value || null);
                setViewAsManagerCategoryId(null);
              }}
              className="w-full bg-[#1A1D23] border border-[#2D3139] hover:border-indigo-500/50 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition-colors cursor-pointer"
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="w-full bg-[#1A1D23]/50 border border-[#2D3139] rounded-lg px-2.5 py-1.5 text-xs text-slate-400 font-medium truncate">
              {projects.find(p => p.id === currentProjectId)?.name || 'Aucun projet actif'}
            </div>
          )}
        </div>

        {/* Manager Mode Selector */}
        {currentUser.role === 'admin' && (
          <div className="px-3 py-2 border-b border-[#2D3139] bg-[#0A0B0E]/20 flex flex-col gap-1.5 flex-none">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-amber-500" />
                Mode de Vue
              </span>
              {viewAsManagerCategoryId && (
                <button
                  onClick={() => setViewAsManagerCategoryId(null)}
                  className="text-[9px] font-semibold text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
            <select
              value={viewAsManagerCategoryId || ''}
              onChange={(e) => setViewAsManagerCategoryId(e.target.value || null)}
              className={`w-full bg-[#1A1D23] border rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none transition-all cursor-pointer ${
                viewAsManagerCategoryId 
                  ? 'border-amber-500/50 hover:border-amber-400 focus:border-amber-500 ring-2 ring-amber-500/10' 
                  : 'border-[#2D3139] hover:border-indigo-500/50 focus:border-indigo-500'
              }`}
            >
              <option value="">Administrateur (Tous)</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  Manager : {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="flex-1 px-3 py-2 overflow-y-auto custom-scrollbar space-y-1">

          {/* ── Groupe 1 : Opérations ───────────── */}
          <button
            onClick={() => toggleSection('operations')}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-300 hover:bg-[#1A1D23] transition-all group"
          >
            <span>⚡ Opérations</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openSections.operations ? 'rotate-180' : ''}`} />
          </button>
          {openSections.operations && (
            <div className="space-y-0.5 pl-1">
              <button
                onClick={() => handleTabChange('dashboard')}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'dashboard' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'} ${guideHighlightTab === 'dashboard' ? 'ring-2 ring-inset ring-amber-400/60' : ''}`}
              >
                <LayoutDashboard className="w-4 h-4 flex-none" />
                <span className="font-medium">Dashboard</span>
              </button>
              <button
                onClick={() => handleTabChange('matrix')}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'matrix' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'} ${guideHighlightTab === 'matrix' ? 'ring-2 ring-inset ring-amber-400/60' : ''}`}
              >
                <Calendar className="w-4 h-4 flex-none" />
                <span className="font-medium">Planning Matrix</span>
              </button>
              <button
                onClick={() => handleTabChange('team_timeline')}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'team_timeline' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'} ${guideHighlightTab === 'team_timeline' ? 'ring-2 ring-inset ring-amber-400/60' : ''}`}
              >
                <Clock className="w-4 h-4 flex-none" />
                <span className="font-medium">Team Timeline</span>
              </button>
              <button
                onClick={() => handleTabChange('slot_planner')}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'slot_planner' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
              >
                <Clock className="w-4 h-4 flex-none" />
                <span className="font-medium">Vue Créneaux</span>
              </button>
              <button
                onClick={() => handleTabChange('map')}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'map' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'} ${guideHighlightTab === 'map' ? 'ring-2 ring-inset ring-amber-400/60' : ''}`}
              >
                <Map className="w-4 h-4 flex-none" />
                <span className="font-medium">Live Map</span>
              </button>
            </div>
          )}

          {/* ── Groupe 2 : Communication ────────── */}
          <button
            onClick={() => toggleSection('communication')}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-300 hover:bg-[#1A1D23] transition-all"
          >
            <span>💬 Communication</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openSections.communication ? 'rotate-180' : ''}`} />
          </button>
          {openSections.communication && (
            <div className="space-y-0.5 pl-1">
              <button
                onClick={() => handleTabChange('messages')}
                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'messages' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
              >
                <Bell className="w-4 h-4 flex-none" />
                <span className="font-medium">Messages</span>
                {myUnreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">
                    {myUnreadCount}
                  </span>
                )}
              </button>
              {(isAdmin || isSuperAdmin) && (
                <button
                  onClick={() => handleTabChange('team_offers')}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'team_offers' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                >
                  <Gift className="w-4 h-4 flex-none text-indigo-400" />
                  <span className="font-medium">Team Offers</span>
                </button>
              )}
            </div>
          )}

          {/* ── Groupe 3 : Artistes & VIP ──────── */}
          {canManageArtists && (
            <>
              <button
                onClick={() => toggleSection('artists')}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-amber-600 hover:text-amber-400 hover:bg-[#1A1D23] transition-all"
              >
                <span>🎭 Artistes & VIP</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openSections.artists ? 'rotate-180' : ''}`} />
              </button>
              {openSections.artists && (
                <div className="space-y-0.5 pl-1">
                  <button
                    onClick={() => handleTabChange('artist_management')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'artist_management' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/20' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Music className="w-4 h-4 flex-none text-amber-400" />
                    <span className="font-medium">Artist Management</span>
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Groupe 4 : Configuration ─────────── */}
          {isAdmin && (
            <>
              <button
                onClick={() => toggleSection('config')}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-300 hover:bg-[#1A1D23] transition-all"
              >
                <span>⚙️ Configuration</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openSections.config ? 'rotate-180' : ''}`} />
              </button>
              {openSections.config && (
                <div className="space-y-0.5 pl-1">
                  <button
                    onClick={() => handleTabChange('team_conf')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'team_conf' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'} ${guideHighlightTab === 'team_conf' ? 'ring-2 ring-inset ring-amber-400/60' : ''}`}
                  >
                    <Users className="w-4 h-4 flex-none" />
                    <span className="font-medium">Manage Team</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('points_conf')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'points_conf' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'} ${guideHighlightTab === 'points_conf' ? 'ring-2 ring-inset ring-amber-400/60' : ''}`}
                  >
                    <MapPin className="w-4 h-4 flex-none" />
                    <span className="font-medium">Op Points</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('tasks_conf')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'tasks_conf' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <ClipboardList className="w-4 h-4 flex-none" />
                    <span className="font-medium">Tâches</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('categories_conf')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'categories_conf' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Settings className="w-4 h-4 flex-none" />
                    <span className="font-medium">Catégories</span>
                  </button>
                  {showAppearance && (
                    <button
                      onClick={() => handleTabChange('appearance')}
                      className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'appearance' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                    >
                      <Palette className="w-4 h-4 flex-none" />
                      <span className="font-medium">Apparence</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleTabChange('custom_fields')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'custom_fields' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <FileText className="w-4 h-4 flex-none" />
                    <span className="font-medium">Champs personnalisés</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('import_team')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'import_team' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Upload className="w-4 h-4 flex-none" />
                    <span className="font-medium">Import Team</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('import_artists')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'import_artists' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Upload className="w-4 h-4 flex-none" />
                    <span className="font-medium">Import Artistes</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('import_places')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'import_places' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Map className="w-4 h-4 flex-none" />
                    <span className="font-medium">Importer lieu et événements</span>
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Groupe 5 : Superadmin uniquement ── */}
          {isSuperAdmin && (
            <>
              <button
                onClick={() => toggleSection('superadmin')}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-rose-400 hover:bg-[#1A1D23] transition-all"
              >
                <span>🔐 Superadmin</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openSections.superadmin ? 'rotate-180' : ''}`} />
              </button>
              {openSections.superadmin && (
                <div className="space-y-0.5 pl-1">
                  <button
                    onClick={() => handleTabChange('data_management')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'data_management' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Database className="w-4 h-4 flex-none" />
                    <span className="font-medium">Import / Export</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('organizations')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'organizations' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Building2 className="w-4 h-4 flex-none" />
                    <span className="font-medium">Organisations</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('audit')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'audit' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <Bell className="w-4 h-4 flex-none" />
                    <span className="font-medium">Audits & Notifications</span>
                  </button>
                  <button
                    onClick={() => handleTabChange('embed_map')}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${activeTab === 'embed_map' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-400 hover:text-white hover:bg-[#1A1D23]'}`}
                  >
                    <ExternalLink className="w-4 h-4 flex-none" />
                    <span className="font-medium">Embed Map</span>
                  </button>
                </div>
              )}
            </>
          )}
        </nav>

                
        {/* Account Switcher & User Info */}
        <div className="p-3 border-t border-[#2D3139] bg-[#0A0B0E]/50 relative">
          {/* Account Switcher Dropdown */}
          {isAccountSwitcherOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-2 bg-[#1C1F26] border border-[#2D3139] rounded-xl shadow-2xl overflow-hidden z-50">
              <div className="px-3 py-2 border-b border-[#2D3139]">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Changer de compte</span>
              </div>
              <div className="py-1">
                {/* Admin / Manager mode */}
                <button
                  onClick={() => { setImpersonatedTeamId(null); setIsAccountSwitcherOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs transition-all hover:bg-[#2D3139] ${
                    !impersonatedTeamId ? 'text-indigo-400 bg-indigo-500/10' : 'text-slate-300'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-none ${
                    !impersonatedTeamId ? 'bg-indigo-500/20' : 'bg-[#2D3139]'
                  }`}>
                    <Shield className={`w-3.5 h-3.5 ${isAdmin ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="font-bold truncate">
                      {userTeamMember?.name || (isSuperAdmin ? 'Superadmin' : currentUser.role === 'admin' ? 'Administrateur' : 'Manager')}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{normalizedUserEmail || currentOrg?.name}</div>
                    {normalizedUserEmail && <div className="text-[10px] text-slate-600 truncate">{currentOrg?.name}</div>}
                  </div>
                  {!impersonatedTeamId && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-400 flex-none" />}
                </button>

                {/* Team mode */}
                {userTeamMember && !userTeamMember.isArtist && (
                  <button
                    onClick={() => { setImpersonatedTeamId(userTeamMember.id); setIsAccountSwitcherOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs transition-all hover:bg-[#2D3139] ${
                      impersonatedTeamId === userTeamMember.id ? 'text-indigo-400 bg-indigo-500/10' : 'text-slate-300'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-none ${
                      impersonatedTeamId === userTeamMember.id ? 'bg-indigo-500/20' : 'bg-[#2D3139]'
                    }`}>
                      <UserCircle className="w-3.5 h-3.5 text-sky-400" />
                    </div>
                    <div className="text-left min-w-0">
                      <div className="font-bold truncate">Mon Espace Team</div>
                      <div className="text-[10px] text-slate-500 truncate">{userTeamMember.name}</div>
                    </div>
                    {impersonatedTeamId === userTeamMember.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-400 flex-none" />}
                  </button>
                )}

                {/* Artist mode */}
                {userTeamMember && userTeamMember.isArtist && (
                  <button
                    onClick={() => { setImpersonatedTeamId(userTeamMember.id); setIsAccountSwitcherOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs transition-all hover:bg-[#2D3139] ${
                      impersonatedTeamId === userTeamMember.id ? 'text-indigo-400 bg-indigo-500/10' : 'text-slate-300'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-none ${
                      impersonatedTeamId === userTeamMember.id ? 'bg-indigo-500/20' : 'bg-[#2D3139]'
                    }`}>
                      <Music className="w-3.5 h-3.5 text-pink-400" />
                    </div>
                    <div className="text-left min-w-0">
                      <div className="font-bold truncate">Mon Espace Artiste</div>
                      <div className="text-[10px] text-slate-500 truncate">{userTeamMember.name}</div>
                    </div>
                    {impersonatedTeamId === userTeamMember.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-400 flex-none" />}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Switcher trigger row */}
          <button
            onClick={() => setIsAccountSwitcherOpen(prev => !prev)}
            className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-xl transition-all hover:bg-[#1A1D23] group mb-2 ${
              isAccountSwitcherOpen ? 'bg-[#1A1D23]' : ''
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-none ${
              impersonatedTeamId ? 'bg-sky-500/15' : 'bg-indigo-500/15'
            }`}>
              {impersonatedTeamId
                ? (userTeamMember?.isArtist
                    ? <Music className="w-4 h-4 text-pink-400" />
                    : <UserCircle className="w-4 h-4 text-sky-400" />)
                : <Shield className={`w-4 h-4 ${isAdmin ? 'text-emerald-400' : 'text-amber-400'}`} />}
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-xs font-semibold text-slate-200 truncate">
                {impersonatedTeamId
                  ? (userTeamMember?.isArtist ? 'Espace Artiste' : 'Espace Team')
                  : (userTeamMember?.name || (isSuperAdmin ? 'Superadmin' : currentUser.role === 'admin' ? 'Administrateur' : 'Manager'))}
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {impersonatedTeamId
                  ? userTeamMember?.name
                  : (normalizedUserEmail
                      ? `${normalizedUserEmail} · ${isSuperAdmin ? 'Superadmin' : currentUser.role === 'admin' ? 'Admin' : 'Manager'}`
                      : (currentOrg?.name || 'Chargement...'))}
              </div>
            </div>
            {userTeamMember && (
              <ChevronDown className={`w-3.5 h-3.5 text-slate-500 flex-none transition-transform duration-200 ${
                isAccountSwitcherOpen ? 'rotate-180' : ''
              }`} />
            )}
          </button>

          {/* Notification + Logout row */}
          <div className="flex items-center justify-between px-1">
            <button
              onClick={requestPermission}
              disabled={!userTeamMember}
              title={!userTeamMember ? "Créez votre profil team pour activer les notifications" : (fcmError || fcmStep)}
              className={`p-1.5 rounded transition-all ${
                permissionState === 'granted' && fcmReady
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : !userTeamMember
                  ? 'text-slate-600 opacity-50 cursor-not-allowed'
                  : 'text-slate-400 hover:text-white hover:bg-[#2D3139]'
              }`}
            >
              <Bell className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleLogout}
              className="p-1.5 hover:bg-[#2D3139] rounded text-slate-400 hover:text-white transition-colors flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider"
              title="Déconnexion"
            >
              <LogOut className="w-3.5 h-3.5" />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 h-[100dvh] overflow-hidden bg-[#0A0B0E]">
        {/* Mobile Header */}
        <div className="md:hidden flex-none flex items-center justify-between p-4 bg-[#14161B] border-b border-[#2D3139] z-10">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 -ml-2 text-slate-400 hover:text-white rounded-lg hover:bg-[#1A1D23]"
            >
              <Menu className="w-6 h-6" />
            </button>
            {appLogo ? (
              <div className="flex items-center gap-1.5">
                <img src={appLogo} alt="Logo" className="max-h-8 max-w-[120px] object-contain" />
                <span className="text-[18px] font-bold tracking-tight text-white ml-1" style={{ fontFamily: '"Fredoka", "Comic Sans MS", cursive, sans-serif' }}>
                  Teams
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <img src={orgLogo} alt="Logo" className="h-8 w-auto" />
                <span className="text-[18px] font-bold tracking-tight text-white ml-1" style={{ fontFamily: '"Fredoka", "Comic Sans MS", cursive, sans-serif' }}>
                  Teams
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
             {isAdmin && (
               <button
                 onClick={() => setMessagingDrawerOpen(!messagingDrawer.isOpen)}
                 className={`p-1.5 rounded-lg transition-all relative ${
                   messagingDrawer.isOpen ? 'text-indigo-400 bg-indigo-500/10' : 'text-indigo-400 hover:text-indigo-300 hover:bg-[#1A1D23]'
                 }`}
                 title="Diffusion / Quick Messaging"
              >
                 <MessageSquare className="w-5 h-5" />
                 {myUnreadCount > 0 && (
                   <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full animate-orange-blink border-2 border-[#0A0B0E]" />
                 )}
              </button>
            )}
            {currentUser.role === 'admin' && userTeamMember ? (
               <button
                 onClick={() => setImpersonatedTeamId(userTeamMember.id)}
                 className="p-1.5 rounded-lg text-indigo-400 hover:text-indigo-300 hover:bg-[#1A1D23] transition-all relative flex items-center justify-center"
                 title="Mon Espace Team"
               >
                 <UserCircle className="w-5 h-5" />
               </button>
             ) : (
               <button
                 onClick={() => setActiveTab('messages')}
                 className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#1A1D23] transition-all relative"
                 title="Mes Messages"
               >
                 <MessageSquare className="w-5 h-5 text-slate-400" />
                 {myUnreadCount > 0 && (
                   <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-[#14161B]" />
                 )}
               </button>
             )}
             <Shield className={`w-5 h-5 ${isAdmin ? 'text-emerald-400' : 'text-amber-400'}`} />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden min-h-0 relative flex flex-col">
          {/* Impersonation Banner */}
          {isAdmin && isSuperAdmin && currentOrgId && organizations.length > 0 && (
            <div className="bg-indigo-600/10 border-b border-indigo-500/20 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-bold text-indigo-400 uppercase tracking-widest">
                <Building2 className="w-3 h-3" />
                Organisation active : <span className="text-white">{organizations.find(o => o.id === currentOrgId)?.name}</span>
              </div>
              {activeTab !== 'organizations' && (
                <button 
                  onClick={() => setActiveTab('organizations')}
                  className="text-[10px] font-bold text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                >
                  Changer <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
          )}

          {/* Manager Simulation Banner */}
          {viewAsManagerCategoryId && (
            <div className="bg-amber-600/10 border-b border-amber-500/20 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-bold text-amber-500 uppercase tracking-widest">
                <Eye className="w-3 h-3 animate-pulse text-amber-400" />
                Simulation : Vue Manager de la catégorie <span className="text-white ml-1">{categories.find(c => c.id === viewAsManagerCategoryId)?.name}</span>
              </div>
              <button 
                onClick={() => setViewAsManagerCategoryId(null)}
                className="text-[10px] font-bold text-amber-500 hover:text-amber-400 flex items-center gap-1 border border-amber-500/20 hover:border-amber-500/40 bg-amber-500/5 px-2.5 py-1 rounded transition-all"
              >
                Quitter la simulation
              </button>
            </div>
          )}
          
          {/* Setup Guide Welcome Banner */}
          {showGuideBanner && activeTab === 'dashboard' && (
            <SetupGuideBanner
              categoryName={myCategory?.name}
              onStart={() => { guide.open(); }}
            />
          )}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {activeTab === 'dashboard' && <DashboardView />}
            {activeTab === 'matrix' && <MatrixView />}
            {activeTab === 'team_timeline' && <GanttTimeline onNavigate={() => setActiveTab('slot_planner')} />}
            {activeTab === 'slot_planner' && <SlotPlannerView onNavigateBack={() => setActiveTab('team_timeline')} />}
            {activeTab === 'points_conf' && <PointsConfig />}
            {activeTab === 'tasks_conf' && <TasksConfig />}
            {activeTab === 'team_conf' && <TeamConfig />}
            {activeTab === 'map' && (
              <div className="h-full flex flex-col">
                {isAdmin && (
                  <div className="bg-[#14161B] border-b border-[#2D3139] px-4 py-2 flex items-center justify-end gap-2 text-xs">
                    <span className="text-slate-500 uppercase tracking-widest font-bold">Moteur :</span>
                    <div className="flex bg-[#0A0B0E] p-1 rounded-lg border border-[#2D3139]">
                      <button 
                        onClick={() => updateGlobalSettings({ mapProvider: 'google' })}
                        className={`px-3 py-1 rounded-md transition-all ${globalSettings.mapProvider === 'google' ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                      >
                        Google
                      </button>
                      <button 
                        onClick={() => updateGlobalSettings({ mapProvider: 'mapbox' })}
                        className={`px-3 py-1 rounded-md transition-all ${globalSettings.mapProvider === 'mapbox' || !globalSettings.mapProvider ? 'bg-indigo-500 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                      >
                        Mapbox
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex-1 overflow-hidden">
                  {globalSettings.mapProvider === 'google' ? <MapView /> : <MapViewMapbox />}
                </div>
              </div>
            )}
            {activeTab === 'messages' && <MessagesView />}
            {isAdmin && activeTab === 'categories_conf' && <CategoriesConfig />}
            {isSuperAdmin && activeTab === 'data_management' && <DataManagement />}
            {isAdmin && activeTab === 'appearance' && <AppearanceSettings />}
            {isAdmin && activeTab === 'custom_fields' && <CustomFieldsConfig />}
            {isAdmin && activeTab === 'import_team' && <ImportTeam />}
            {isAdmin && activeTab === 'import_artists' && <ImportArtists />}
            {isAdmin && activeTab === 'import_places' && <ImportPlaces />}
            {isSuperAdmin && activeTab === 'organizations' && <OrganizationsConfig />}
            {isSuperAdmin && activeTab === 'audit' && <AuditView />}
            {isSuperAdmin && activeTab === 'embed_map' && (
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="p-6 max-w-4xl mx-auto">
                  <EmbedMapConfig />
                </div>
              </div>
            )}
            {(isAdmin || isSuperAdmin) && activeTab === 'team_offers' && <TeamOffersView />}
            {canManageArtists && activeTab === 'artist_management' && <ArtistManagementView />}

          </div>
        </div>

        {/* Setup Guide Panel */}
        {currentUser.role === 'manager' && !guide.state.completed && (
          <SetupGuide
            state={guide.state}
            onClose={guide.close}
            onNext={guide.next}
            onPrev={guide.prev}
            onGoToStep={guide.goToStep}
            onVisitStep={guide.visitStep}
            onComplete={guide.complete}
            onNavigate={handleGuideNavigate}
          />
        )}

        {/* Floating re-open button */}
        {currentUser.role === 'manager' && !guide.state.completed && !guide.state.open && (
          <SetupGuideFloatButton
            step={guide.state.step}
            total={5}
            onClick={guide.open}
          />
        )}

        {/* Floating Messaging Drawer */}
        <MessagingDrawer />

        {/* Project Management Modal */}
        <ProjectManagementModal 
          isOpen={isProjectModalOpen} 
          onClose={() => setIsProjectModalOpen(false)} 
        />

        {/* Desktop Quick Messaging Header Icon */}
        {isAdmin && (
          <div className="hidden md:flex fixed top-4 right-6 z-[60] items-center gap-3">
            <button
              onClick={() => setMessagingDrawerOpen(!messagingDrawer.isOpen)}
              className={`p-2 bg-[#14161B]/80 backdrop-blur border border-[#2D3139] hover:bg-[#1A1D23] hover:border-slate-600 rounded-xl transition-all shadow-lg flex items-center justify-center ${
                messagingDrawer.isOpen ? 'text-indigo-400 bg-indigo-500/10 border-indigo-500/30 shadow-[0_0_15px_rgba(99,102,241,0.1)]' : 'text-indigo-400 hover:text-indigo-300'
              }`}
                            title="Diffusion / Quick Messaging"
            >
              <MessageSquare className="w-5 h-5" />
              {myUnreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-orange-500 rounded-full animate-orange-blink border-2 border-[#14161B]" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
